<script setup lang="ts">
import { computed } from 'vue'
import { cn } from '@/lib/utils'
import { useBreakpoint } from '@/composables/useBreakpoint'

/**
 * JLayout - 기본 레이아웃 컴포넌트 (templates)
 * Base Layout Component
 *
 * @description
 * 공통 레이아웃 구조를 제공하는 기본 컴포넌트입니다.
 * Header, Sidebar, Content 영역을 슬롯으로 제공하여 유연한 레이아웃 구성이 가능합니다.
 * 모바일에서는 사이드바가 오버레이 드로어로 전환됩니다.
 *
 * 레이아웃 구조:
 * - header: 상단 헤더 영역 (슬롯)
 * - sidebar: 사이드바 영역 (슬롯)
 * - content: 메인 콘텐츠 영역 (슬롯, 기본 슬롯도 지원)
 *
 * @example
 * ```vue
 * <JLayout styletype="default" :content-scroll="true">
 *   <template #header>
 *     <JHeader logo-text="JWMS Portal" />
 *   </template>
 *   <template #sidebar>
 *     <JSidebarSimple :menu-items="menuItems" />
 *   </template>
 *   <template #content>
 *     <div>메인 콘텐츠</div>
 *   </template>
 * </JLayout>
 * ```
 */

type StyleType = 'default' | 'minimal'

const props = withDefaults(
  defineProps<{
    /** 레이아웃 스타일 타입 */
    styletype?: StyleType
    /** 콘텐츠 영역 스크롤 가능 여부 */
    contentScroll?: boolean
    /** 추가 CSS 클래스 */
    class?: string
    /** 모바일에서 사이드바를 오버레이 드로어로 표시 */
    sidebarOverlay?: boolean
    /** 사이드바 오버레이 열림 상태 (sidebarOverlay=true일 때만 사용) */
    sidebarOpen?: boolean
  }>(),
  {
    styletype: 'default',
    contentScroll: true,
    sidebarOverlay: false,
    sidebarOpen: false,
  }
)

const emit = defineEmits<{
  'backdrop-click': []
}>()

const { isMobile } = useBreakpoint()

// 실제 오버레이 모드: prop으로 명시하거나 모바일 자동 감지
const isOverlayMode = computed(() => props.sidebarOverlay || isMobile.value)

/**
 * 스타일 프리셋
 */
const STYLE_PRESETS: Record<StyleType, {
  containerClass: string
  contentClass: string
}> = {
  default: {
    containerClass: 'flex flex-col h-screen w-full overflow-hidden',
    contentClass: 'flex flex-1 overflow-hidden',
  },
  minimal: {
    containerClass: 'flex flex-col h-screen w-full overflow-hidden',
    contentClass: 'flex flex-1 overflow-hidden',
  },
}

const preset = computed(() => {
  return STYLE_PRESETS[props.styletype] ?? STYLE_PRESETS.default
})

const contentClasses = computed(() => {
  return cn(
    preset.value.contentClass,
    !props.contentScroll && 'overflow-hidden'
  )
})

const handleBackdropClick = () => {
  emit('backdrop-click')
}
</script>

<template>
  <div :class="cn(preset.containerClass, props.class)">
    <!-- 헤더 슬롯 -->
    <slot name="header" />

    <!-- 메인 컨텐츠 영역 -->
    <div :class="contentClasses">
      <!-- 사이드바: 데스크톱은 인라인, 모바일은 오버레이 -->
      <template v-if="isOverlayMode">
        <!-- 모바일 오버레이 드로어 -->
        <Transition name="j-sidebar-backdrop">
          <div
            v-if="sidebarOpen"
            class="fixed inset-0 z-40 bg-black/40"
            @click="handleBackdropClick"
          />
        </Transition>
        <Transition name="j-sidebar-drawer">
          <div
            v-if="sidebarOpen"
            class="fixed inset-y-0 left-0 z-50 w-[280px] max-w-[80vw] shadow-xl"
          >
            <slot name="sidebar" />
          </div>
        </Transition>
      </template>
      <template v-else>
        <!-- 데스크톱 인라인 사이드바 -->
        <slot name="sidebar" />
      </template>

      <!-- 콘텐츠 슬롯 -->
      <div class="flex-1 flex flex-col min-h-0" :class="props.contentScroll ? 'overflow-auto' : 'overflow-hidden'">
        <slot name="content">
          <!-- 기본 슬롯도 지원 -->
          <slot />
        </slot>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 사이드바 드로어 슬라이드 애니메이션 */
.j-sidebar-drawer-enter-active,
.j-sidebar-drawer-leave-active {
  transition: transform 0.25s ease;
}
.j-sidebar-drawer-enter-from,
.j-sidebar-drawer-leave-to {
  transform: translateX(-100%);
}

/* 백드롭 페이드 애니메이션 */
.j-sidebar-backdrop-enter-active,
.j-sidebar-backdrop-leave-active {
  transition: opacity 0.25s ease;
}
.j-sidebar-backdrop-enter-from,
.j-sidebar-backdrop-leave-to {
  opacity: 0;
}
</style>
